import { createSlice } from '@reduxjs/toolkit';
import { BodyPartSecondLevel } from '../model';
import {
  deleteSymptom,
  fetchSecondLevelBodyParts,
  fetchSymptoms,
  fetchSymptomsByType,
  insertSymptoms,
  searchSymptoms,
  searchSymptomsByBodyPart,
  updateSymptom,
} from './actions';
import { Symptom, SymptomSearchResult } from './model';

export interface SymptomState {
  symptomSearchResults: SymptomSearchResult[];
  loadingSearchSymptoms: boolean;
  symptoms: Symptom[];
  symptomsByType: Symptom[];
  loadingSymptoms: boolean;
  loadingSymptomsByType: boolean;
  loadingInsertSymptoms: boolean;
  secondLevelBodyParts?: BodyPartSecondLevel[];
}

const initialState: SymptomState = {
  symptomSearchResults: [],
  loadingSearchSymptoms: false,
  symptoms: [],
  symptomsByType: [],
  loadingSymptoms: false,
  loadingSymptomsByType: false,
  loadingInsertSymptoms: false,
  secondLevelBodyParts: undefined,
};

const symptomSlice = createSlice({
  name: 'symptom',
  initialState,
  reducers: {
    clearSymptomSearchResults: (state) => {
      state.symptomSearchResults = [];
    },
    clearSymptoms: (state) => {
      state.loadingSymptoms = true;
      state.symptoms = [];
    },
    clearSecondLevelBodyParts: (state) => {
      state.secondLevelBodyParts = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSymptoms.pending, (state) => {
        state.loadingSymptoms = true;
      })
      .addCase(fetchSymptoms.fulfilled, (state, actions) => {
        state.symptoms = actions.payload;
        state.loadingSymptoms = false;
      })
      .addCase(fetchSymptoms.rejected, (state) => {
        state.loadingSymptoms = false;
      })
      .addCase(fetchSymptomsByType.pending, (state) => {
        state.loadingSymptomsByType = true;
      })
      .addCase(fetchSymptomsByType.fulfilled, (state, actions) => {
        state.symptomsByType = actions.payload;
        state.loadingSymptomsByType = false;
      })
      .addCase(fetchSymptomsByType.rejected, (state) => {
        state.loadingSymptomsByType = false;
      })
      .addCase(insertSymptoms.pending, (state) => {
        state.loadingInsertSymptoms = true;
      })
      .addCase(insertSymptoms.fulfilled, (state) => {
        state.loadingInsertSymptoms = false;
      })
      .addCase(insertSymptoms.rejected, (state) => {
        state.loadingInsertSymptoms = false;
      })
      .addCase(searchSymptoms.pending, (state) => {
        state.loadingSearchSymptoms = true;
      })
      .addCase(searchSymptoms.fulfilled, (state, action) => {
        state.symptomSearchResults = action.payload;
        state.loadingSearchSymptoms = false;
      })
      .addCase(searchSymptoms.rejected, (state) => {
        state.symptomSearchResults = [];
        state.loadingSearchSymptoms = false;
      })
      .addCase(updateSymptom.fulfilled, (state, action) => {
        const updatedSymptoms = state.symptoms.map((symptom) =>
          symptom.id === action.payload.id ? action.payload : symptom
        );
        state.symptoms = updatedSymptoms;
      })
      .addCase(deleteSymptom.fulfilled, (state, action) => {
        state.symptoms = state.symptoms.filter(
          (symptom) => symptom.id !== action.meta.arg
        );
      })
      .addCase(searchSymptomsByBodyPart.pending, (state) => {
        state.loadingSearchSymptoms = false;
      })
      .addCase(searchSymptomsByBodyPart.fulfilled, (state, action) => {
        state.symptomSearchResults = action.payload;
        state.loadingSearchSymptoms = false;
      })
      .addCase(searchSymptomsByBodyPart.rejected, (state) => {
        state.symptomSearchResults = [];
        state.loadingSearchSymptoms = false;
      })
      .addCase(fetchSecondLevelBodyParts.fulfilled, (state, action) => {
        state.secondLevelBodyParts = action.payload;
      });
  },
});

export default symptomSlice.reducer;

export const {
  clearSymptomSearchResults,
  clearSymptoms,
  clearSecondLevelBodyParts,
} = symptomSlice.actions;
